import { useState } from 'react';
import { toast } from 'react-toastify';
import { useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useFormik, FormikProvider } from 'formik';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/Auth';
import { useModalContext } from '../../context/Modal';

import { Button, Card, Icon } from '../../components/UI';
import { Input } from '../../components/form';
import {
  CHANGE_DEFAULT_PROFILE_USERNAME,
  REQUEST_USER_REMOVAL,
  CANCEL_MEMBERSHIP,
  COMPLETE_USER_REMOVAL_VERIFICATION,
} from '../../http/apollo-queries';

import { IMembership } from '../../types/model';

import { usernameValidator } from '../ChooseUsername/username.validator';
import { emailValidator } from '../../components/form/Input/validators/email-validator';
import { ConfirmAction } from '../../components/Modal';
import { pascalCase } from '../../helpers/functions';
import UsernameHint from './UsernameHint';
import OtpModal from './OtpModal';
import { logger } from '../../helpers/logger';

const AccountMainProfile = () => {
  const { t } = useTranslation();
  const { user, refetch, changeEmail } = useAuth();
  const navigate = useNavigate();
  const { setElement, closeModal } = useModalContext();

  const [avatarHovered, setAvatarHovered] = useState(false);
  const [bypassToken, setBypassToken] = useState<string>();

  const [cancelMembership] = useMutation<{ cancelMembership: IMembership }>(
    CANCEL_MEMBERSHIP,
    {
      onCompleted: refetch,
    }
  );
  const [requestUserRemoval] = useMutation(REQUEST_USER_REMOVAL);
  const [completeUserRemoval] = useMutation(COMPLETE_USER_REMOVAL_VERIFICATION);

  const isPaidSubscriber = user?.membership?.paid;
  const defaultProfile = user?.profiles.items.find((p) => p.default);

  const [changeUsername, { loading: changeUsernameLoading }] = useMutation(
    CHANGE_DEFAULT_PROFILE_USERNAME
  );

  const [usernameHintShown, setUsernameHintShown] = useState(false);

  const formik = useFormik({
    initialValues: {
      username: user?.username || '',
      email: user?.email || '',
    },
    onSubmit: (values) => {
      if (defaultProfile?.name !== values.username) {
        changeUsername({
          variables: {
            username: values.username,
          },
        })
          .then((res) => {
            toast.success(t('account.mainProfile.form.success.saveUsername'));
            refetch();
          })
          .catch((err) => {
            logger.error(pascalCase(err.message));
            toast.error(
              t(
                'account.mainProfile.form.error.' + pascalCase(err.message),
                'account.mainProfile.form.error.saveUsername'
              )
            );
          });
      }

      if (user?.email !== values.email) {
        changeEmail(values.email)
          .then((res) => {
            toast.success(t('account.mainProfile.form.success.changeEmail'));
            navigate('/logout');
          })
          .catch((err) => {
            logger.error(pascalCase(err.message));
            t(
              'account.mainProfile.form.error.' + pascalCase(err.message),
              'account.mainProfile.form.error.changeEmail'
            );
          });
      }
    },
    validateOnMount: true,
  });

  const openAccountDeleteModal = useCallback(() => {
    if (isPaidSubscriber) {
      const confirmedTitle = t('modals.removeSubscription.confirmedTitle');
      const confirmedSubtitle = t(
        'modals.removeSubscription.confirmedSubtitle'
      );
      const title = t('modals.removeSubscription.title');
      const subtitle = t('modals.removeSubscription.subtitle');

      const showError = () => {
        toast.error(t('modals.removeSubscription.error'));
        closeModal();
      };

      const handleRemoveSubscription = async () => {
        try {
          const response = await cancelMembership();
          if (
            response.data?.cancelMembership.id &&
            !response.data?.cancelMembership.active
          ) {
            return true;
          } else {
            showError();
          }
        } catch (error) {
          showError();
        }
      };

      setElement(
        <ConfirmAction
          confirmedSubtitle={confirmedSubtitle}
          confirmedTitle={confirmedTitle}
          subtitle={subtitle}
          icon="warning"
          title={title}
          save={handleRemoveSubscription}
          saveButtonClassName="text-red-700 border-red-700 border-1 rounded-lg hover:bg-primary hover:border-primary"
          saveButtonVariant=""
          saveButtonName={t('modals.removeSubscription.agree')}
          cancelButtonName={t('modals.removeSubscription.cancel')}
          cancelButtonClassName="border-grey-30 border-1 rounded-lg hover:bg-primary hover:border-primary"
          cancelButtonVariant=""
          footerClassName="flex-row-reverse gap-4"
        />
      );
    } else {
      const confirmedTitle = t('modals.deleteMainProfile.confirmedTitle');
      const confirmedSubtitle = t('modals.deleteMainProfile.confirmedSubtitle');
      const title = t('modals.deleteMainProfile.title');
      const subtitle = t('modals.deleteMainProfile.subtitle');

      const showError = (messageId: string) => {
        toast.error(t(messageId));
        closeModal();
      };

      const createCompleteUserDeletionHandler =
        (resolve: (value: unknown) => any, reject: (value: unknown) => any) =>
        (code: string) => {
          completeUserRemoval({
            variables: {
              code,
              bypassToken,
            },
          }).then(({ data }: any) => {
            if (
              data.requestRemoval &&
              data.completeUserRemovalVerification.status === 'SUCCESS'
            ) {
              resolve(true);
            } else {
              reject('modals.deleteMainProfile.errorCompleting');
            }
          });
        };

      setElement(
        <ConfirmAction
          confirmedSubtitle={confirmedSubtitle}
          confirmedTitle={confirmedTitle}
          save={() => {
            return new Promise((resolve, reject) => {
              requestUserRemoval().then(({ data }: any) => {
                if (
                  data.requestRemoval &&
                  data.requestRemoval.removalStatus &&
                  data.requestRemoval.bypassToken
                ) {
                  setBypassToken(data.requestRemoval.bypassToken);
                  setElement(
                    <OtpModal
                      onSubmit={createCompleteUserDeletionHandler(
                        resolve,
                        reject
                      )}
                    />
                  );
                } else {
                  reject('modals.deleteMainProfile.errorRequesting');
                }
              });
            }).catch((messageId) => showError(messageId));
          }}
          finish={() => navigate('/logout')}
          subtitle={subtitle}
          icon="warning"
          title={title}
          saveButtonClassName="w-40 text-red-700 border-red-700 border-1 rounded-lg hover:bg-primary hover:border-primary"
          saveButtonVariant=""
          saveButtonName={t('modals.deleteMainProfile.agree')}
          cancelButtonName={t('modals.deleteMainProfile.cancel')}
          cancelButtonClassName="w-40 text-grey-70 border-grey-70 border-1 rounded-lg hover:bg-primary hover:border-primary"
          cancelButtonVariant=""
        />
      );
    }
  }, [
    isPaidSubscriber,
    cancelMembership,
    requestUserRemoval,
    closeModal,
    setElement,
    t,
  ]);

  return (
    <Card
      className="mt-4"
      title={t('account.mainProfile.title')}
      menuItems={[
        <span className="-m-4 p-4" onClick={openAccountDeleteModal}>
          {t(
            isPaidSubscriber
              ? 'modals.removeSubscription.menuEntry'
              : 'account.mainProfile.delete'
          )}
        </span>,
      ]}
    >
      <FormikProvider value={formik}>
        <form autoComplete="off" onSubmit={formik.handleSubmit}>
          <div className="flex flex-col md:flex-row justify-start align-center">
            <div className={'mx-auto mb-5 md:mb-0 md:mr-10'}>
              <div
                className="relative rounded-full w-32 h-32 bg-grey-10"
                onMouseEnter={() => {
                  setAvatarHovered(true);
                }}
                onMouseLeave={() => {
                  setAvatarHovered(false);
                }}
              >
                {typeof defaultProfile?.avatar?.size?.avatarLarge ===
                  'string' && (
                  <img
                    className="rounded-full w-32 h-32 object-cover"
                    src={defaultProfile?.avatar?.size?.avatarLarge}
                    alt={defaultProfile?.avatar?.filename}
                  />
                )}

                <div
                  className={
                    'bg-black bg-opacity-50 rounded-full flex items-center cursor-pointer justify-center top-0 left-0 absolute w-full h-full ' +
                    (avatarHovered ? '' : 'hidden')
                  }
                  onClick={() => {
                    navigate('/upload-image?profileId=' + defaultProfile?.id);
                  }}
                >
                  <Icon
                    className="h-12 text-white pointer-events-none"
                    color="black"
                    svgName={'circlePencil'}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-5 w-full pt-5">
              <div className="relative flex flex-col md:flex-row gap-10">
                <Input
                  size="full"
                  name="username"
                  id="username"
                  formik={formik}
                  validate={usernameValidator}
                  onBlur={() => setUsernameHintShown(false)}
                  onFocus={() => setUsernameHintShown(true)}
                  type="text"
                  label={t('account.mainProfile.form.label.username')}
                  placeholder={t(
                    'account.mainProfile.form.placeholder.username'
                  )}
                  prefix="@"
                />
                <UsernameHint
                  show={usernameHintShown}
                  status={formik.errors.username && formik.touched.username}
                />
                <Input
                  size="full"
                  name="email"
                  id="email"
                  formik={formik}
                  type="text"
                  validate={emailValidator}
                  label={t('account.mainProfile.form.label.email')}
                  placeholder={t('account.mainProfile.form.label.email')}
                  errorMessage={
                    formik.touched.email && formik.errors.email
                      ? t('account.mainProfile.form.error.email')
                      : ''
                  }
                  assistiveText={
                    formik.touched.email && formik.values.email !== user?.email
                      ? t('account.mainProfile.form.assistive.email')
                      : ''
                  }
                />
              </div>
              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  loading={changeUsernameLoading}
                >
                  Save changes
                </Button>
              </div>
            </div>
          </div>
        </form>
      </FormikProvider>
    </Card>
  );
};
export { AccountMainProfile };
